import { useCallback, useEffect, useMemo, useState } from 'react'
import config from 'constants/config'
import { ChertoExperiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import useHasMounted from 'hooks/useHasMounted'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff } from 'selectors/accountSelectors'

export type HotelPackageSelectionVariant = 'control' | 'option-d'

interface Options {
  /** The supported product cohort, independent of the visitor's assigned arm. */
  isEligible: boolean
  /** A sentinel shared by Control and D at the package area. */
  isInView: boolean
  isReady?: boolean
  /** Assignment ownership can persist while its supported presentation is hidden. */
  canExpose?: boolean
}

export function shouldDefaultPackageSelectionPreviewToOptionD(buildEnvironment: string | undefined, hostname: string): boolean {
  return buildEnvironment === 'development' && (
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]' || hostname === '::1'
  )
}

export default function useHotelPackageSelectionExperiment({ isEligible, isInView, isReady = true, canExpose = true }: Options) {
  const { variation, triggerActivation } = useExperiment(ChertoExperiments.croSppPackageSelection, { canActivate: false })
  const hasMounted = useHasMounted()
  const isStaff = useAppSelector(isLEStaff)
  const [previewVariant, setPreviewVariantState] = useState<HotelPackageSelectionVariant>()
  // Preview controls appear for staff after mounting. Only local
  // development defaults to D; deployed pages retain their assigned arm.
  const isPreviewAvailable = hasMounted && isStaff
  const defaultsToOptionD = isPreviewAvailable && shouldDefaultPackageSelectionPreviewToOptionD(process.env.NODE_ENV, window.location.hostname)
  const assignedVariant = variation === 'control' || variation === 'option-d' ? variation : undefined
  const hasConfigOverride = config[`CHERTO_${ChertoExperiments.croSppPackageSelection.id}`] !== undefined
  const activePreviewVariant = isPreviewAvailable ? previewVariant : undefined
  const effectiveVariant = activePreviewVariant ?? (defaultsToOptionD ? 'option-d' : assignedVariant)
  const variant = isEligible ? effectiveVariant ?? 'control' : 'control'

  useEffect(() => {
    if (!isStaff) setPreviewVariantState(undefined)
  }, [isStaff])

  const setPreviewVariant = useCallback((nextVariant: HotelPackageSelectionVariant) => {
    if (isPreviewAvailable) setPreviewVariantState(nextVariant)
  }, [isPreviewAvailable])

  useEffect(() => {
    if (activePreviewVariant !== undefined || process.env.NODE_ENV === 'development' || hasConfigOverride || !isEligible || !isInView || !isReady || !canExpose || !assignedVariant) return
    triggerActivation()
  }, [activePreviewVariant, assignedVariant, hasConfigOverride, isEligible, isInView, isReady, canExpose, triggerActivation])

  return useMemo(() => ({
    variant,
    isOptionD: variant === 'option-d',
    isPreviewAvailable,
    previewVariant,
    setPreviewVariant,
  }), [variant, isPreviewAvailable, previewVariant, setPreviewVariant])
}
